import React, { useState, useMemo } from "react";
import { X, MapPin, Briefcase, ArrowRight, Share2, Copy, Check, QrCode } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import confetti from "canvas-confetti";
import { QRCodeSVG } from "qrcode.react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { Chip } from "./brutal";

/** Inline SVG icons for each platform — match brand palette without 3rd-party logos. */
const PlatformIcon = ({ name }) => {
  const cls = "w-6 h-6";
  switch (name) {
    case "WhatsApp":
      return <svg className={cls} viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.501-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0 0 20.464 3.488"/></svg>;
    case "LinkedIn":
      return <svg className={cls} viewBox="0 0 24 24" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.063 2.063 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>;
    case "X":
      return <svg className={cls} viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>;
    case "Facebook":
      return <svg className={cls} viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>;
    default:
      return null;
  }
};

const ShareButton = ({ icon, label, color, onClick, dataTestId }) => (
  <button
    onClick={onClick}
    className={`btn-brutal ${color} w-14 h-14 p-0 flex-shrink-0 justify-center transition-transform duration-150 active:scale-90 hover:-translate-y-0.5`}
    title={label}
    aria-label={label}
    data-testid={dataTestId}
  >
    {icon}
  </button>
);

const DEFAULT_SHARE_TEMPLATES = [
  "🚨 Urgent need — {title}{location_clause}. If you know someone right for it, send them my way:",
  "💛 Heart-led role: {title}{location_clause}. Could be perfect for someone you know:",
  "✨ Career changer? We're hiring {title}{location_clause}. Think it could be a good fit?",
];

const renderTemplate = (tpl, job) =>
  (tpl || "")
    .replaceAll("{title}", job?.title || "the role")
    .replaceAll("{location_clause}", job?.location ? ` in ${job.location}` : "")
    .replaceAll("{location}", job?.location || "");

export default function JobDetailModal({ job, open, onClose }) {
  const nav = useNavigate();
  const { user } = useAuth();
  const [copied, setCopied] = useState(false);
  const [showQR, setShowQR] = useState(false);
  const [toneIdx, setToneIdx] = useState(0);

  const shareUrl = useMemo(() => {
    if (!job || !user) return "";
    const base = window.location.origin;
    // Route shares through /api/share/job/{id} so social crawlers (FB, LinkedIn, WhatsApp,
    // X, iMessage) see Open Graph tags + preview image. Real users are redirected to
    // /apply/{id}?ref=... immediately by the server-rendered page.
    return `${base}/api/share/job/${job.id}?ref=${user.id}`;
  }, [job, user]);

  const templates = useMemo(() => {
    const raw = (job?.share_templates && job.share_templates.length > 0) ? job.share_templates : DEFAULT_SHARE_TEMPLATES;
    return raw.map((t) => renderTemplate(t, job));
  }, [job]);

  const shareText = templates[toneIdx] || templates[0] || "";

  if (!open || !job) return null;

  // Fire-and-forget share tracking — never blocks the share action.
  const trackShare = (platform) => {
    api.post(`/jobs/${job.id}/share-track?platform=${encodeURIComponent(platform)}`).catch(() => {});
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Link copied — paste anywhere!");
      trackShare("copy");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't copy — long-press the link instead");
    }
  };

  const openShare = (url) => window.open(url, "_blank", "noopener,noreferrer,width=600,height=700");

  const onWhatsapp = () => { trackShare("whatsapp"); openShare(`https://wa.me/?text=${encodeURIComponent(`${shareText} ${shareUrl}`)}`); };
  const onLinkedin = () => { trackShare("linkedin"); openShare(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`); };
  const onTwitter = () => { trackShare("x"); openShare(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`); };
  const onFacebook = () => { trackShare("facebook"); openShare(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareText)}`); };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4 animate-in fade-in duration-200"
      onClick={onClose}
      data-testid="job-detail-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white border-t-2 sm:border-2 border-black sm:rounded-2xl rounded-t-2xl shadow-brutal w-full sm:max-w-2xl max-h-[92vh] overflow-y-auto animate-in slide-in-from-bottom-6 sm:slide-in-from-bottom-0 sm:zoom-in-95 duration-300"
      >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b-2 border-black px-5 py-4 flex items-start justify-between">
          <div className="flex items-start gap-4">
            <div className="w-20 h-20 sm:w-24 sm:h-24 border-2 border-black rounded-2xl overflow-hidden flex items-center justify-center bg-brand-blue text-white shadow-brutal flex-shrink-0">
              {job.logo_url ? (
                <img src={job.logo_url} alt={job.title} className="w-full h-full object-cover" />
              ) : (
                <Briefcase className="w-9 h-9" strokeWidth={2.5} />
              )}
            </div>
            <div>
              <h2 className="font-display font-black text-2xl leading-tight" data-testid="job-modal-title">{job.title}</h2>
              <div className="mt-1 flex flex-wrap gap-2 items-center">
                {job.status === "open" ? <Chip color="bg-brand-green">OPEN</Chip> : <Chip color="bg-neutral-300">CLOSED</Chip>}
                {job.location && (
                  <span className="inline-flex items-center gap-1 text-xs font-semibold text-neutral-700">
                    <MapPin className="w-3 h-3" /> {job.location}
                  </span>
                )}
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 border-2 border-black rounded-xl bg-white hover:bg-neutral-100 flex items-center justify-center flex-shrink-0"
            data-testid="job-modal-close"
            aria-label="Close"
          >
            <X className="w-4 h-4" strokeWidth={2.5} />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-5 space-y-5">
          <section>
            <h3 className="text-xs font-bold uppercase tracking-widest text-neutral-500 mb-2">About the role</h3>
            <p className="text-sm text-neutral-800 whitespace-pre-line leading-relaxed" data-testid="job-modal-description">{job.description || "No description provided."}</p>
          </section>

          {job.requirements && (
            <section>
              <h3 className="text-xs font-bold uppercase tracking-widest text-neutral-500 mb-2">Requirements</h3>
              <p className="text-sm text-neutral-800 whitespace-pre-line leading-relaxed" data-testid="job-modal-requirements">{job.requirements}</p>
            </section>
          )}

          {job.status === "open" && (
            <>
              <section className="brutal p-4 bg-brand-yellow">
                <div className="flex items-center gap-2 mb-2">
                  <Share2 className="w-4 h-4" strokeWidth={2.5} />
                  <h3 className="font-display font-black text-base">Share & earn</h3>
                </div>
                <p className="text-xs text-neutral-700 mb-3 leading-relaxed">
                  Share your unique link below. When a candidate applies through it, the referral is automatically attributed to you and you earn points as they progress.
                </p>

                {templates.length > 1 && (
                  <div className="mb-3" data-testid="share-tone-picker">
                    <div className="text-[10px] uppercase tracking-widest font-bold text-neutral-700 mb-1.5">Pick a tone</div>
                    <div className="flex flex-wrap gap-1.5">
                      {templates.map((_, i) => {
                        const labels = ["🚨 Urgent", "💛 Heart-led", "✨ Career changer"];
                        const label = labels[i] || `Tone ${i + 1}`;
                        return (
                          <button
                            key={i}
                            onClick={() => setToneIdx(i)}
                            className={`text-[11px] font-bold px-2.5 py-1 rounded-full border-2 border-black transition-colors ${toneIdx === i ? "bg-brand-ink text-white" : "bg-white hover:bg-neutral-100"}`}
                            data-testid={`share-tone-${i}`}
                          >
                            {label}
                          </button>
                        );
                      })}
                    </div>
                    <textarea
                      readOnly
                      value={shareText}
                      className="input-brutal text-xs w-full mt-2 leading-relaxed"
                      rows={2}
                      onFocus={(e) => e.target.select()}
                      data-testid="share-text-preview"
                    />
                  </div>
                )}

                <div className="flex items-center gap-2">
                  <input
                    readOnly
                    value={shareUrl}
                    className="input-brutal text-xs flex-1 truncate"
                    onFocus={(e) => e.target.select()}
                    data-testid="share-link-input"
                  />
                  <button
                    onClick={copy}
                    className="btn-brutal btn-white w-11 h-11 p-0 flex-shrink-0 justify-center"
                    title="Copy link"
                    aria-label="Copy link"
                    data-testid="share-copy-btn"
                  >
                    {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </button>
                  <button
                    onClick={() => { setShowQR((v) => { if (!v) trackShare("qr"); return !v; }); }}
                    className={`btn-brutal w-11 h-11 p-0 flex-shrink-0 justify-center ${showQR ? "bg-black text-white border-black" : "btn-white"}`}
                    title="Show QR code for printing / in-person sharing"
                    aria-label="Show QR code"
                    aria-pressed={showQR}
                    data-testid="share-qr-btn"
                  >
                    <QrCode className="w-4 h-4" />
                  </button>
                </div>
                {showQR && (
                  <div className="mt-3 brutal bg-white p-4 flex items-center gap-4 animate-in fade-in zoom-in-95 duration-200" data-testid="share-qr-block">
                    <div className="bg-white p-2 border-2 border-black rounded-xl flex-shrink-0">
                      <QRCodeSVG value={shareUrl} size={120} level="M" marginSize={0} bgColor="#FFFFFF" fgColor="#09090B" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[10px] uppercase tracking-widest font-bold text-neutral-500">Scan to apply</div>
                      <p className="text-xs text-neutral-700 leading-relaxed mt-1">
                        Print this on a breakroom poster, stick it in a WhatsApp group, or hand it to someone in person. Every scan is tagged to you.
                      </p>
                    </div>
                  </div>
                )}
                <div className="flex items-center gap-2 mt-3">
                  <ShareButton icon={<PlatformIcon name="WhatsApp" />} label="WhatsApp" color="bg-[#25D366] text-white border-black" onClick={onWhatsapp} dataTestId="share-whatsapp" />
                  <ShareButton icon={<PlatformIcon name="LinkedIn" />} label="LinkedIn" color="bg-[#0A66C2] text-white border-black" onClick={onLinkedin} dataTestId="share-linkedin" />
                  <ShareButton icon={<PlatformIcon name="X" />} label="X (Twitter)" color="bg-black text-white border-black" onClick={onTwitter} dataTestId="share-twitter" />
                  <ShareButton icon={<PlatformIcon name="Facebook" />} label="Facebook" color="bg-[#1877F2] text-white border-black" onClick={onFacebook} dataTestId="share-facebook" />
                </div>
              </section>

              <button
                onClick={() => {
                  // celebratory burst before navigating
                  try {
                    confetti({
                      particleCount: 90,
                      spread: 80,
                      origin: { y: 0.7 },
                      colors: ["#FF5C00", "#0047FF", "#00E676", "#FFD600", "#FF3AC5"],
                      ticks: 150,
                    });
                  } catch { /* no-op if confetti fails */ }
                  setTimeout(() => { onClose(); nav(`/app/refer?jobId=${job.id}`); }, 250);
                }}
                className="btn-brutal btn-primary w-full py-3 text-base transition-transform duration-150 active:scale-95"
                data-testid="job-modal-refer-btn"
              >
                Refer a candidate <ArrowRight className="w-4 h-4" />
              </button>
            </>
          )}

          {job.status !== "open" && (
            <div className="brutal p-4 bg-neutral-100 text-center text-sm text-neutral-700">
              This role is closed and no longer accepting applications.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
